<section class="ui-table-block">
  @if (showToolbar()) {
    <header class="ui-table-toolbar">
      @if (showSearch()) {
        <div class="ui-table-toolbar__search-row">
          <span class="ui-table-toolbar__search-label">{{ 'Search' | uiLabelTranslate }}</span>

          <div class="ui-table-toolbar__search-wrap">
            <span class="ui-table-toolbar__search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="6"></circle>
                <path d="M20 20l-4.2-4.2"></path>
              </svg>
            </span>
            <ui-form-field
              class="ui-table-toolbar__search"
              label=""
              type="search"
              [placeholder]="searchPlaceholder()"
              [value]="searchQuery()"
              (valueChange)="onSearchChange($event)"
            ></ui-form-field>
          </div>
        </div>
      }

      <div class="ui-table-toolbar__controls">
        @if (showSelection() && showBulkDeleteButton()) {
          <ui-button variant="danger" [disabled]="selectedRows().length === 0" (click)="triggerBulkDelete()">
            {{ bulkDeleteLabel() }}
          </ui-button>
        }

        @if (showFilter() && filterFields().length > 0) {
          <ui-button variant="secondary" (click)="openFilterPanel()">
            <span class="ui-table-toolbar__filter-button">
              <span class="ui-table-toolbar__filter-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M3 5h18l-7 8v6l-4-2v-4L3 5z"></path>
                </svg>
              </span>
              <span>{{ 'Filters' | uiLabelTranslate }}</span>
              <span class="ui-table-toolbar__filter-count">{{ appliedFilterCount() }}</span>
            </span>
          </ui-button>
        }
      </div>
    </header>
  }

  <div class="ui-table-wrap">
    <table class="ui-table" [attr.aria-label]="ariaLabel()">
      <colgroup>
        @if (showSelection()) {
          <col class="ui-table__selection-col" />
        }

        @for (column of columns(); track column.key) {
          <col [style.width]="column.width || null" />
        }

        @if (showActionsColumn()) {
          <col class="ui-table__actions-col" />
        }
      </colgroup>

      <thead>
        <tr>
          @if (showSelection()) {
            <th class="ui-table__selection-head">
              <input
                type="checkbox"
                [checked]="allDisplayedSelected()"
                (change)="toggleAllDisplayed(masterCheck.checked)"
                #masterCheck
              />
            </th>
          }

          @for (column of columns(); track column.key) {
            <th
              [style.width]="column.width || null"
              [style.minWidth]="column.width || null"
              [style.maxWidth]="column.width || null"
              [attr.data-align]="column.align || 'left'"
              [class.ui-table__head--sortable]="showSort() && column.sortable"
              (click)="showSort() ? toggleColumnSort(column) : null"
            >
              <span class="ui-table__head-text">{{ column.label | uiLabelTranslate }}</span>
              @if (showSort() && column.sortable) {
                <span class="ui-table__sort-icon">{{ sortIconFor(column) }}</span>
              }
            </th>
          }

          @if (showActionsColumn()) {
            <th class="ui-table__actions-head">{{ actionsColumnLabel() | uiLabelTranslate }}</th>
          }
        </tr>
      </thead>

      @if (pagedRows().length === 0) {
        <tbody>
          <tr>
            <td
              class="ui-table__empty"
              [attr.colspan]="columns().length + (showSelection() ? 1 : 0) + (showActionsColumn() ? 1 : 0)"
            >
              {{ emptyMessage() | uiLabelTranslate }}
            </td>
          </tr>
        </tbody>
      } @else {
        <tbody>
          @for (row of pagedRows(); track trackRow($index, row)) {
            <tr [class.ui-table__row--clickable]="isRowClickable()" (click)="onRowClick($event, row)">
              @if (showSelection()) {
                <td class="ui-table__selection-cell">
                  <input
                    type="checkbox"
                    [checked]="isRowSelected(row)"
                    (change)="toggleRowSelection($event, row, rowCheck.checked)"
                    #rowCheck
                  />
                </td>
              }

              @for (column of columns(); track column.key) {
                <td
                  [style.width]="column.width || null"
                  [style.minWidth]="column.width || null"
                  [style.maxWidth]="column.width || null"
                  [attr.data-align]="column.align || 'left'"
                  [class.ui-table__cell--first-hover]="shouldShowFirstColumnHover(column)"
                  [class.ui-table__cell--first-clickable]="shouldShowFirstColumnHover(column) && column.clickable"
                >
                  @if (column.type === 'actions' && column.actions) {
                    <div class="ui-table__actions">
                      @for (action of column.actions; track action.id) {
                        <ui-button
                          [variant]="action.variant || 'ghost'"
                          size="sm"
                          [ariaLabel]="action.label | uiLabelTranslate"
                          [class.ui-table__icon-action]="isIconOnlyAction(action)"
                          [disabled]="isActionDisabled(action, row)"
                          (click)="onActionClick($event, action.id, row)"
                        >
                          @if (isIconOnlyAction(action)) {
                            <span class="ui-table__action-icon" aria-hidden="true">
                              <svg viewBox="0 0 24 24">
                                <path [attr.d]="actionIconPath(action)"></path>
                              </svg>
                            </span>
                          } @else {
                            {{ action.label | uiLabelTranslate }}
                          }
                        </ui-button>
                      }
                    </div>
                  } @else if (column.type === 'status') {
                    <span
                      class="ui-table__badge"
                      [attr.data-tone]="badgeToneFor(textOf(row, column))"
                      [style.--ui-badge-bg]="badgeBgFor(textOf(row, column))"
                      [style.--ui-badge-text]="badgeTextFor(textOf(row, column))"
                    >
                      {{ textOf(row, column) }}
                    </span>
                  } @else if (column.type === 'stock') {
                    <span
                      class="ui-table__badge"
                      [attr.data-tone]="badgeToneFor(textOf(row, column))"
                      [style.--ui-badge-bg]="badgeBgFor(textOf(row, column))"
                      [style.--ui-badge-text]="badgeTextFor(textOf(row, column))"
                    >
                      {{ textOf(row, column) }}
                    </span>
                  } @else if (column.type === 'toggle') {
                    <ui-toggle
                      [checked]="isToggleChecked(column, row)"
                      [disabled]="isToggleDisabled(column, row)"
                      [ariaLabel]="toggleAriaLabel(column, row)"
                      (toggled)="onToggleChange(column, row, $event)"
                    ></ui-toggle>
                  } @else if (column.clickable) {
                    <button type="button" class="ui-table__link" (click)="onCellClick($event, column, row)">
                      {{ textOf(row, column) }}
                    </button>
                  } @else {
                    {{ textOf(row, column) }}
                  }
                </td>
              }

              @if (showActionsColumn()) {
                <td class="ui-table__actions-cell">
                  <div class="ui-table__actions">
                    @for (action of rowActions(); track action.id) {
                      <ui-button
                        [variant]="action.variant || 'ghost'"
                        size="sm"
                          [ariaLabel]="action.label | uiLabelTranslate"
                        [class.ui-table__icon-action]="isIconOnlyAction(action)"
                        [disabled]="isActionDisabled(action, row)"
                        (click)="onActionClick($event, action.id, row)"
                      >
                        @if (isIconOnlyAction(action)) {
                          <span class="ui-table__action-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24">
                              <path [attr.d]="actionIconPath(action)"></path>
                            </svg>
                          </span>
                        } @else {
                          {{ action.label | uiLabelTranslate }}
                        }
                      </ui-button>
                    }
                  </div>
                </td>
              }
            </tr>
          }
        </tbody>
      }
    </table>
  </div>

  @if (showPagination()) {
    <footer class="ui-table-pagination">
      <span class="ui-table-pagination__meta ui-table-pagination__chip">
        {{ 'Showing' | uiLabelTranslate }} {{ pageMeta().start }}-{{ pageMeta().end }} {{ 'of' | uiLabelTranslate }} {{ pageMeta().total }}
      </span>

      <div class="ui-table-pagination__controls">
        @if (showPageSizeSelector()) {
          <div class="ui-table-pagination__size-group" [attr.aria-label]="'Rows per page' | uiLabelTranslate">
            <span class="ui-table-pagination__size-label">{{ 'Rows per page' | uiLabelTranslate }}</span>
            <ui-select
              class="ui-table-pagination__size"
              [label]="''"
              [required]="true"
              [value]="pageSizeValue()"
              [options]="pageSizeSelectOptions()"
              (valueChange)="setPageSize($event)"
            ></ui-select>
          </div>
        }

        <ui-button variant="secondary" [disabled]="!canGoPrevious()" (click)="previousPage()">{{ 'Previous' | uiLabelTranslate }}</ui-button>
        <ui-button variant="secondary" [disabled]="!canGoNext()" (click)="nextPage()">{{ 'Next' | uiLabelTranslate }}</ui-button>
      </div>
    </footer>
  }

  <section class="ui-table-drawer-layer" [class.ui-table-drawer-layer--open]="isAnyDrawerOpen()">
    <button
      type="button"
      class="ui-table-drawer-layer__backdrop"
      [attr.aria-hidden]="!isAnyDrawerOpen()"
      (click)="closeActiveDrawer()"
    ></button>

    <aside
      class="ui-table-drawer ui-table-drawer--filter"
      [class.ui-table-drawer--open]="isFilterDrawerOpen()"
      [attr.aria-hidden]="!isFilterDrawerOpen()"
    >
      <header class="ui-table-drawer__header">
        <div>
          <h3>{{ 'Filter Drawer' | uiLabelTranslate }}</h3>
          <p>{{ 'Refine table results using reusable field configuration.' | uiLabelTranslate }}</p>
        </div>
        <ui-button variant="ghost" (click)="closeFilterPanel()">{{ 'Close' | uiLabelTranslate }}</ui-button>
      </header>

      <div class="ui-table-drawer__body">
        <section class="ui-table-drawer__content">
          @for (field of filterFields(); track field.key) {
            @if (field.type === 'text') {
              <ui-form-field
                [label]="field.label | uiLabelTranslate"
                [placeholder]="(field.placeholder || '') | uiLabelTranslate"
                [value]="asText(filterValue(field.key))"
                (valueChange)="setFilterDraftText(field.key, $event)"
              ></ui-form-field>
            }

            @if (field.type === 'number') {
              <ui-form-field
                [label]="field.label | uiLabelTranslate"
                type="number"
                [placeholder]="(field.placeholder || '') | uiLabelTranslate"
                [value]="asText(filterValue(field.key))"
                (valueChange)="setFilterDraftText(field.key, $event)"
              ></ui-form-field>
            }

            @if (field.type === 'select') {
              <ui-select
                [label]="field.label | uiLabelTranslate"
                [placeholder]="(field.placeholder || 'Select option') | uiLabelTranslate"
                [value]="asText(filterValue(field.key))"
                [options]="field.options || []"
                (valueChange)="setFilterDraftText(field.key, $event)"
              ></ui-select>
            }

            @if (field.type === 'autocomplete') {
              <ui-autocomplete-field
                [label]="field.label | uiLabelTranslate"
                [placeholder]="(field.placeholder || 'Search option') | uiLabelTranslate"
                [value]="asText(filterValue(field.key))"
                [options]="filterAutocompleteOptions(field)"
                (valueChange)="setFilterDraftText(field.key, $event)"
                (optionSelected)="setFilterDraftText(field.key, $event)"
              ></ui-autocomplete-field>
            }

            @if (field.type === 'radio') {
              <fieldset class="ui-table-drawer__field-group">
                <legend>{{ field.label | uiLabelTranslate }}</legend>

                <div class="ui-table-drawer__radio-list">
                  @for (option of field.options || []; track option.value) {
                    <label class="ui-table-drawer__radio-option">
                      <input
                        type="radio"
                        [name]="'table-filter-' + field.key"
                        [checked]="filterValue(field.key) === option.value"
                        (change)="setFilterDraftText(field.key, option.value)"
                      />
                      <span>{{ option.label | uiLabelTranslate }}</span>
                    </label>
                  }
                </div>
              </fieldset>
            }

            @if (field.type === 'toggle') {
              <div class="ui-table-drawer__toggle-field">
                <div>
                  <span class="ui-table-drawer__field-label">{{ field.label | uiLabelTranslate }}</span>
                  @if (field.placeholder) {
                    <p>{{ field.placeholder | uiLabelTranslate }}</p>
                  }
                </div>

                <ui-toggle
                  [checked]="filterValue(field.key) === true"
                  [ariaLabel]="field.label | uiLabelTranslate"
                  (toggled)="setFilterDraftToggle(field.key, $event)"
                ></ui-toggle>
              </div>
            }
          }
        </section>
      </div>

      <footer class="ui-table-drawer__footer ui-table-drawer__footer--split">
        <ui-button variant="secondary" (click)="resetFilters()">{{ 'Reset' | uiLabelTranslate }}</ui-button>
        <ui-button (click)="applyFilters()">{{ 'Apply Filters' | uiLabelTranslate }}</ui-button>
      </footer>
    </aside>

    <aside
      class="ui-table-drawer ui-table-drawer--detail"
      [class.ui-table-drawer--open]="isDetailDrawerOpen()"
      [attr.aria-hidden]="!isDetailDrawerOpen()"
    >
      <header class="ui-table-drawer__header">
        <div>
          <h3>{{ selectedRowTitle() || ('Record Detail' | uiLabelTranslate) }}</h3>
          <p>{{ 'Record details are rendered through shared table configuration.' | uiLabelTranslate }}</p>
        </div>
        <ui-button variant="ghost" (click)="closeDetailPanel()">{{ 'Close' | uiLabelTranslate }}</ui-button>
      </header>

      <div class="ui-table-drawer__body">
        @if (activeDetailTab(); as tab) {
          @if (resolvedDetailTabs().length > 1) {
            <nav class="ui-table-drawer__tabs" [attr.aria-label]="'Detail sections' | uiLabelTranslate">
              @for (detailTab of resolvedDetailTabs(); track detailTab.id) {
                <button
                  type="button"
                  class="ui-table-drawer__tab"
                  [class.ui-table-drawer__tab--active]="detailTab.id === tab.id"
                  (click)="selectDetailTab(detailTab.id)"
                >
                  {{ detailTab.label | uiLabelTranslate }}
                </button>
              }
            </nav>
          }

          <section class="ui-table-drawer__content">
            @if (selectedRow() && detailSectionsFor(tab).length > 0) {
              @for (section of detailSectionsFor(tab); track section.id) {
                <article class="ui-table-detail-section">
                  @if (section.title || section.description) {
                    <header class="ui-table-detail-section__header">
                      @if (section.title) {
                        <h4>{{ section.title | uiLabelTranslate }}</h4>
                      }

                      @if (section.description) {
                        <p>{{ section.description | uiLabelTranslate }}</p>
                      }
                    </header>
                  }

                  <div class="ui-table-detail-section__fields">
                    @for (field of section.fields; track field.key) {
                      <div class="ui-table-detail-field">
                        <span class="ui-table-detail-field__label">{{ field.label | uiLabelTranslate }}</span>

                        @if (field.type === 'status') {
                          <span
                            class="ui-table__badge"
                            [attr.data-tone]="badgeToneFor(detailTextOf(selectedRow(), field))"
                            [style.--ui-badge-bg]="badgeBgFor(detailTextOf(selectedRow(), field))"
                            [style.--ui-badge-text]="badgeTextFor(detailTextOf(selectedRow(), field))"
                          >
                            {{ detailTextOf(selectedRow(), field) }}
                          </span>
                        } @else if (field.type === 'stock') {
                          <span
                            class="ui-table__badge"
                            [attr.data-tone]="badgeToneFor(detailTextOf(selectedRow(), field))"
                            [style.--ui-badge-bg]="badgeBgFor(detailTextOf(selectedRow(), field))"
                            [style.--ui-badge-text]="badgeTextFor(detailTextOf(selectedRow(), field))"
                          >
                            {{ detailTextOf(selectedRow(), field) }}
                          </span>
                        } @else {
                          <span class="ui-table-detail-field__value">{{ detailTextOf(selectedRow(), field) }}</span>
                        }
                      </div>
                    }
                  </div>
                </article>
              }
            } @else {
              <p class="ui-table-drawer__empty">{{ (tab.emptyMessage || 'No detail content configured for this tab.') | uiLabelTranslate }}</p>
            }
          </section>
        }
      </div>
    </aside>
  </section>
</section>
